import React, { useEffect, useRef, useState } from 'react';
import { Box, BoxProps } from '@mui/material';
import { styled } from '@mui/material/styles';

const AnimatedBoxStyled = styled(Box)(() => ({
  opacity: 0,
  transform: 'translateY(20px)',
  transition: 'opacity 0.6s ease, transform 0.6s ease',
  '&.visible': {
    opacity: 1,
    transform: 'translateY(0)',
  },
}));

interface AnimatedBoxProps extends BoxProps {
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  children: React.ReactNode;
}

export const AnimatedBox: React.FC<AnimatedBoxProps> = ({
  children,
  delay = 0,
  direction = 'up',
  sx,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const transformMap = {
    up: 'translateY(20px)',
    down: 'translateY(-20px)',
    left: 'translateX(20px)',
    right: 'translateX(-20px)',
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setIsVisible(true);
            }, delay);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (boxRef.current) {
      observer.observe(boxRef.current);
    }

    return () => {
      if (boxRef.current) {
        observer.unobserve(boxRef.current);
      }
    };
  }, [delay]);

  return (
    <AnimatedBoxStyled
      ref={boxRef}
      className={isVisible ? 'visible' : ''}
      {...props}
      sx={{
        '&:not(.visible)': {
          transform: transformMap[direction],
        },
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
        ...sx,
      }}
    >
      {children}
    </AnimatedBoxStyled>
  );
};

export default AnimatedBox;








